<template>
    <a :href="topUpUrl" target="_blank"
       class="ai-credit-badge"
       :class="{
           'ai-credit-badge--low': isLow,
           'ai-credit-badge--empty': isEmpty,
           'ai-credit-badge--expiring': isExpiring && !isEmpty && !isLow,
       }"
       :title="tooltip">
        <q-icon :name="isExpiring && !isEmpty && !isLow ? 'schedule' : 'bolt'" size="14px" />
        <span class="ai-credit-badge__amount">{{ formattedBalance }}</span>
        <span class="ai-credit-badge__currency">JOD</span>
    </a>
</template>

<script>
import { aiCreditsState } from '../../boot/aiCredits'

export default {
    name: 'AiCreditBadge',
    computed: {
        balance() {
            return parseFloat(aiCreditsState.balanceJod) || 0
        },
        formattedBalance() {
            return this.balance.toFixed(3)
        },
        isLow() {
            return this.balance > 0 && this.balance < 0.05
        },
        isEmpty() {
            return this.balance <= 0
        },
        topUpUrl() {
            return aiCreditsState.topUpUrl || '/checkout?type=ai-credit'
        },
        /**
         * Whole days until the wallet's promise ends, or null when nothing can
         * expire. Safari 12 safe: no optional chaining, no nullish coalescing.
         */
        daysToExpiry() {
            const exp = aiCreditsState.expiry
            if (!exp || !exp.enabled || !exp.expiresAt) return null
            const ends = new Date(exp.expiresAt)
            if (isNaN(ends.getTime())) return null
            const ms = ends.getTime() - Date.now()
            return Math.max(0, Math.ceil(ms / 86400000))
        },
        /**
         * «Soon» is the server's widest warning rung, never a number chosen
         * here — otherwise the badge and the reminder the student actually
         * receives would quietly disagree about when to worry.
         */
        isExpiring() {
            const days = this.daysToExpiry
            if (days === null || this.balance <= 0) return false
            const exp = aiCreditsState.expiry
            const threshold = exp && exp.warnFromDays ? exp.warnFromDays : 60
            return days <= threshold
        },
        tooltip() {
            if (this.isEmpty) return 'انقر لشحن رصيد الذكاء الاصطناعي'
            if (this.isLow) return 'الرصيد منخفض — انقر للشحن'
            if (this.isExpiring) {
                return 'ينتهي رصيدك بعد ' + this.daysToExpiry
                    + ' يومًا إن لم تستخدم الذكاء الاصطناعي — أي استخدام يجدّد المهلة'
            }
            return 'رصيد الذكاء الاصطناعي — انقر لشحن المزيد'
        },
    },
}
</script>

<style scoped>
.ai-credit-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 9999px;
    background: linear-gradient(135deg, #dbeafe, #ede9fe);
    color: #1e40af;
    font-weight: 600;
    font-size: 13px;
    text-decoration: none;
    border: 1px solid #c7d2fe;
    transition: all 0.2s;
    cursor: pointer;
}
.ai-credit-badge:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.ai-credit-badge__currency {
    font-size: 11px;
    opacity: 0.7;
    margin-right: 2px;
}
.ai-credit-badge--low {
    background: linear-gradient(135deg, #ffedd5, #fed7aa);
    color: #9a3412;
    border-color: #fdba74;
}
.ai-credit-badge--empty {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    color: #991b1b;
    border-color: #fca5a5;
}
/* Credit that is about to lapse — distinct from «low», which is about amount. */
.ai-credit-badge--expiring {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #92400e;
    border-color: #fcd34d;
}
</style>
